/**
 * The HTTP wire contract shared by the candidate app, the admin app and the Pages Functions:
 * every route path plus the request and response body of every endpoint in
 * `docs/ARCHITECTURE.md` §6.
 *
 * This module is types plus frozen route builders only — no behaviour. Domain shapes are reused
 * from `./types` rather than restated, so a Firestore field rename can never drift from the wire.
 */

import type {
  AdminRole,
  AdminUser,
  Answer,
  Application,
  ApplicationStatus,
  AppSettings,
  AuditEvent,
  ConsentRecord,
  GlobalVideoSettings,
  Iso,
  MediaAsset,
  MediaKind,
  PublicAppSettings,
  PublicMedia,
  Question,
  QuestionSnapshotItem,
  ResumeRef,
  WorkInterestCategory,
} from './types';

export type { PublicMedia } from './types';

/* ------------------------------------------------------------------------------------------------
 * Routes
 * ---------------------------------------------------------------------------------------------- */

/**
 * Every path served by the candidate Pages Function (`apps/candidate/functions/api/[[route]].ts`).
 * Interpolated ids are percent-encoded, so callers pass raw Firestore ids.
 */
export const CandidateRoutes = {
  health: () => '/api/health',
  config: () => '/api/config',
  applications: () => '/api/applications',
  application: (id: string) => `/api/applications/${encodeURIComponent(id)}`,
  applicationInterests: (id: string) => `/api/applications/${encodeURIComponent(id)}/interests`,
  applicationResume: (id: string) => `/api/applications/${encodeURIComponent(id)}/resume`,
  applicationConsent: (id: string) => `/api/applications/${encodeURIComponent(id)}/consent`,
  applicationQuestionsStart: (id: string) =>
    `/api/applications/${encodeURIComponent(id)}/questions/start`,
  answerUploadUrl: (id: string, questionId: string) =>
    `/api/applications/${encodeURIComponent(id)}/answers/${encodeURIComponent(questionId)}/upload-url`,
  answerAccept: (id: string, questionId: string) =>
    `/api/applications/${encodeURIComponent(id)}/answers/${encodeURIComponent(questionId)}/accept`,
  answerDiscard: (id: string, questionId: string) =>
    `/api/applications/${encodeURIComponent(id)}/answers/${encodeURIComponent(questionId)}/discard`,
  applicationSubmit: (id: string) => `/api/applications/${encodeURIComponent(id)}/submit`,
} as const;

/**
 * Every path served by the admin Pages Function (`apps/admin/functions/api/[[route]].ts`).
 * The minimum role for each is documented in `docs/ARCHITECTURE.md` §6.2 and enforced server-side.
 */
export const AdminRoutes = {
  health: () => '/api/health',
  me: () => '/api/me',

  applications: () => '/api/applications',
  application: (id: string) => `/api/applications/${encodeURIComponent(id)}`,
  applicationReprocess: (id: string) => `/api/applications/${encodeURIComponent(id)}/reprocess`,
  applicationArchive: (id: string) => `/api/applications/${encodeURIComponent(id)}/archive`,

  questions: () => '/api/questions',
  question: (id: string) => `/api/questions/${encodeURIComponent(id)}`,
  questionHardDelete: (id: string) => `/api/questions/${encodeURIComponent(id)}?hard=true`,
  questionsReorder: () => '/api/questions/reorder',

  interests: () => '/api/interests',
  interest: (id: string) => `/api/interests/${encodeURIComponent(id)}`,
  interestsReorder: () => '/api/interests/reorder',

  media: () => '/api/media',
  mediaUploadUrl: () => '/api/media/upload-url',
  mediaItem: (id: string) => `/api/media/${encodeURIComponent(id)}`,
  mediaFinalize: (id: string) => `/api/media/${encodeURIComponent(id)}/finalize`,

  globalVideos: () => '/api/global-videos',

  settings: () => '/api/settings',
  settingsContent: () => '/api/settings/content',
  settingsDisclosure: () => '/api/settings/disclosure',
  settingsSystem: () => '/api/settings/system',

  users: () => '/api/users',
  user: (id: string) => `/api/users/${encodeURIComponent(id)}`,

  audit: () => '/api/audit',
} as const;

/* ------------------------------------------------------------------------------------------------
 * Errors
 * ---------------------------------------------------------------------------------------------- */

/**
 * Machine-readable error codes. Clients branch on these; the human-readable `message` is for
 * display and may change.
 */
export type ApiErrorCode =
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'method_not_allowed'
  | 'conflict'
  | 'payload_too_large'
  | 'unsupported_media_type'
  | 'rate_limited'
  | 'application_closed'
  | 'app_check_failed'
  | 'upstream_error'
  | 'internal_error';

/**
 * The body of every non-2xx response. `code` is normally an {@link ApiErrorCode}, but is typed as
 * `string` so a client never breaks on an code it has not been taught yet.
 */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/** The body of endpoints that acknowledge a mutation without returning a document. */
export interface OkResponse {
  ok: true;
}

/**
 * `GET /api/health` on either app.
 *
 * `ok` is liveness: this Function is deployed and running. It says nothing about configuration,
 * which is why `config` exists beside it — a deployment with not one secret set still answers
 * `ok: true`, and somebody comparing a green health check against a 500 elsewhere has no way to
 * connect the two. `config.ready` is the question they actually meant to ask.
 *
 * The report carries variable **names and verdicts only**, never a value or any fragment of one.
 * Every name in it is already published in the README and in `docs/DEPLOYMENT.md`.
 */
export interface HealthResponse {
  ok: true;
  config?: {
    /** True when nothing required is missing or malformed. */
    ready: boolean;
    /** Names of required variables that are not set. */
    missing: string[];
    /** Required variables that are set but cannot work, each with a canned explanation. */
    malformed: { name: string; state: string; detail?: string }[];
    /** Optional variables worth knowing about, whatever their state. */
    optional: { name: string; state: string; detail?: string }[];
  };
}

/* ------------------------------------------------------------------------------------------------
 * Candidate API — public config
 * ---------------------------------------------------------------------------------------------- */

/** One selectable work-interest category as shown to a candidate. */
export interface PublicInterest {
  id: string;
  label: string;
}

/** The host's four global videos, resolved to playable Stream ids, or `null` when unassigned. */
export interface PublicGlobalVideos {
  welcome: PublicMedia | null;
  intro: PublicMedia | null;
  listening: PublicMedia | null;
  closing: PublicMedia | null;
}

/**
 * `GET /api/config` — everything the candidate app needs before a candidate has an application:
 * copy, active interests, how many questions to expect, the host's videos and the Stream subdomain.
 * `captionLanguage` is the language code the Stream player is asked to show captions in, and is the
 * same code the server generates captions under, so the track the player selects always exists.
 */
export interface PublicConfigResponse {
  settings: PublicAppSettings;
  interests: PublicInterest[];
  questionCount: number;
  globalVideos: PublicGlobalVideos;
  customerSubdomain: string;
  captionLanguage: string;
}

/* ------------------------------------------------------------------------------------------------
 * Candidate API — application lifecycle
 * ---------------------------------------------------------------------------------------------- */

/**
 * `POST /api/applications` — the candidate's name, plus at least one way to reach them.
 *
 * `email` and `phone` are each optional and **at least one must be sent**: a candidate filling
 * this in on a phone may well have a mobile number and no address they read, and requiring an
 * address would quietly exclude them. The server enforces the rule with `validateContactDetails`
 * and returns its message for the candidate to read.
 */
export interface CreateApplicationRequest {
  candidateName: string;
  email?: string;
  phone?: string;
}

/** `POST /api/applications` — the id the candidate app uses for the rest of the session. */
export interface CreateApplicationResponse {
  applicationId: string;
  status: ApplicationStatus;
}

/**
 * `GET /api/applications/:id` — enough state to resume the flow mid-session after a reload:
 * which steps are done, the frozen question set, and which questions already have an answer.
 */
export interface CandidateApplicationStateResponse {
  id: string;
  status: ApplicationStatus;
  candidateName: string;
  interestCategoryIds: string[];
  hasResume: boolean;
  consentAccepted: boolean;
  questionSnapshot: QuestionSnapshotItem[];
  answeredQuestionIds: string[];
}

/** `PATCH /api/applications/:id/interests` — the categories the candidate selected. */
export interface UpdateInterestsRequest {
  interestCategoryIds: string[];
}

/** `PATCH /api/applications/:id/interests` — echoes the labels denormalised onto the application. */
export interface UpdateInterestsResponse {
  ok: true;
  interestLabels: string[];
}

/**
 * `POST /api/applications/:id/resume` — the body is the raw file bytes, not JSON. These fields are
 * carried as the `X-File-Name` and `Content-Type` request headers.
 */
export interface UploadResumeRequest {
  fileName: string;
  contentType: string;
}

/** `POST /api/applications/:id/resume` — the stored resume reference. */
export interface UploadResumeResponse {
  resume: ResumeRef;
}

/** `DELETE /api/applications/:id/resume`. */
export type DeleteResumeResponse = OkResponse;

/** `POST /api/applications/:id/consent` — explicit acceptance of a specific disclosure version. */
export interface AcceptConsentRequest {
  accepted: true;
  consentVersion: string;
}

/** `POST /api/applications/:id/consent` — the consent record written to the application. */
export interface AcceptConsentResponse {
  consent: ConsentRecord;
}

/**
 * `POST /api/applications/:id/questions/start` — idempotent: the first call freezes the active
 * question set onto the application, later calls return the same snapshot.
 */
export interface StartQuestionsResponse {
  questionSnapshot: QuestionSnapshotItem[];
}

/** `POST /api/applications/:id/answers/:questionId/upload-url` — size of the recording in bytes. */
export interface UploadUrlRequest {
  uploadLength: number;
}

/**
 * `POST /api/applications/:id/answers/:questionId/upload-url` — a one-time Cloudflare Stream TUS
 * endpoint the browser PATCHes bytes to directly, plus the Stream id those bytes will land on.
 */
export interface UploadUrlResponse {
  uploadUrl: string;
  streamVideoId: string;
}

/** `POST /api/applications/:id/answers/:questionId/accept` — the take the candidate kept. */
export interface AcceptAnswerRequest {
  streamVideoId: string;
  durationSeconds: number;
}

/** `POST /api/applications/:id/answers/:questionId/accept` — the stored answer document. */
export interface AcceptAnswerResponse {
  answer: Answer;
}

/** `POST /api/applications/:id/answers/:questionId/discard` — a retake, so delete the abandoned video. */
export interface DiscardAnswerRequest {
  streamVideoId: string;
}

/** `POST /api/applications/:id/answers/:questionId/discard`. */
export type DiscardAnswerResponse = OkResponse;

/** `POST /api/applications/:id/submit` — the application moves to `submitted` and AI work begins. */
export interface SubmitApplicationResponse {
  status: ApplicationStatus;
  submittedAt: Iso;
}

/* ------------------------------------------------------------------------------------------------
 * Admin API — identity
 * ---------------------------------------------------------------------------------------------- */

/**
 * `GET /api/me` — the caller's `/adminUsers` document and the role resolved server-side. Auth0
 * claims are never trusted for role.
 */
export interface AdminMeResponse {
  user: AdminUser;
  role: AdminRole;
}

/* ------------------------------------------------------------------------------------------------
 * Admin API — applications
 * ---------------------------------------------------------------------------------------------- */

/**
 * One row of the HR candidate list. `archived` is independent of `status`: an archived candidate
 * keeps the processing status it had, and rows only carry it so the list can mark them when the
 * archived filter is `only` or `all`.
 *
 * `email` and `phone` are both carried and either may be empty, because a candidate gives one or
 * the other or both; `phone` is formatted for reading, not dialling — Altamont calls out, and
 * publishes no number of its own to call in on.
 */
export interface ApplicationListItem {
  id: string;
  candidateName: string;
  email: string;
  phone: string;
  interestLabels: string[];
  status: ApplicationStatus;
  hasResume: boolean;
  answerCount: number;
  archived: boolean;
  createdAt: Iso;
  submittedAt: Iso | null;
}

/**
 * Which archived candidates a listing includes: `exclude` leaves them out (the default, so HR sees
 * the people still in play), `only` shows the archive, `all` shows both.
 */
export type ArchivedFilter = 'exclude' | 'only' | 'all';

/**
 * Query-string parameters of `GET /api/applications`. `search` matches the start of a candidate's
 * name, of their email address or of their phone number; `from` / `to` bound `submittedAt`;
 * `archived` chooses which side of the archive to list and defaults to `exclude`; `cursor`
 * continues a previous page.
 */
export interface ListApplicationsQuery {
  search?: string;
  interest?: string;
  status?: ApplicationStatus;
  from?: Iso;
  to?: Iso;
  archived?: ArchivedFilter;
  limit?: number;
  cursor?: string;
}

/** `GET /api/applications` — one page of candidates, newest first. */
export interface ListApplicationsResponse {
  items: ApplicationListItem[];
  nextCursor: string | null;
}

/**
 * Short-lived signed playback URLs for one answer video. Candidate videos are never public, so
 * these are minted per request and expire.
 */
export interface AnswerPlayback {
  iframeUrl: string;
  hlsUrl: string;
  thumbnailUrl: string;
  expiresAt: Iso;
}

/**
 * `GET /api/applications/:id` — the full candidate record for HR: the application, every answer in
 * question order, signed playback per answer (keyed by answer id, which is the question id) and a
 * signed resume URL when one was uploaded.
 */
export interface AdminApplicationDetailResponse {
  application: Application;
  answers: Answer[];
  playback: Record<string, AnswerPlayback>;
  resumeUrl: string | null;
}

/** `POST /api/applications/:id/reprocess` — re-runs transcription and summarisation. */
export interface ReprocessApplicationResponse {
  ok: true;
  status: ApplicationStatus;
}

/**
 * `POST /api/applications/:id/archive` — archive a candidate who is not a fit for anything open
 * right now, or (`archived: false`) put them back in the active list. `reason` is HR's short note,
 * kept to `MAX_ARCHIVE_REASON_LENGTH` characters and stored on the application.
 */
export interface ArchiveApplicationRequest {
  archived: boolean;
  reason?: string;
}

/**
 * `POST /api/applications/:id/archive` — the archive state after the change. The application's
 * processing `status` is untouched, which is why it is not echoed here.
 */
export interface ArchiveApplicationResponse {
  ok: true;
  archived: boolean;
  archivedAt: Iso | null;
}

/**
 * `DELETE /api/applications/:id` — purges Stream videos, Storage objects and Firestore documents.
 * Archiving is the everyday action; this one is reserved for a deletion requested in writing under
 * state privacy law.
 */
export type DeleteApplicationResponse = OkResponse;

/* ------------------------------------------------------------------------------------------------
 * Admin API — questions
 * ---------------------------------------------------------------------------------------------- */

/** `GET /api/questions` — every question including inactive ones, in `sortOrder`. */
export interface ListQuestionsResponse {
  questions: Question[];
}

/** `POST /api/questions` — a new question; omitted optional fields take their defaults. */
export interface CreateQuestionRequest {
  internalTitle: string;
  questionText: string;
  promptMediaId?: string | null;
  maxDurationSeconds?: number;
  active?: boolean;
}

/** `POST /api/questions`. */
export interface CreateQuestionResponse {
  question: Question;
}

/** `PATCH /api/questions/:id` — any subset of the editable fields; content changes bump `version`. */
export interface UpdateQuestionRequest {
  internalTitle?: string;
  questionText?: string;
  promptMediaId?: string | null;
  maxDurationSeconds?: number;
  active?: boolean;
}

/** `PATCH /api/questions/:id`. */
export interface UpdateQuestionResponse {
  question: Question;
}

/**
 * `DELETE /api/questions/:id` — soft-delete returns the deactivated question; a hard delete
 * (`?hard=true`, admin only) returns `null`.
 */
export interface DeleteQuestionResponse {
  ok: true;
  question: Question | null;
}

/** `POST /api/questions/reorder` and `POST /api/interests/reorder` — ids in their new order. */
export interface ReorderRequest {
  order: string[];
}

/** The applied order, dense and 0-based, after a reorder. */
export interface ReorderResponse {
  ok: true;
  order: string[];
}

/* ------------------------------------------------------------------------------------------------
 * Admin API — work interests
 * ---------------------------------------------------------------------------------------------- */

/** `GET /api/interests` — every category including inactive ones, in `sortOrder`. */
export interface ListInterestsResponse {
  interests: WorkInterestCategory[];
}

/** `POST /api/interests`. */
export interface CreateInterestRequest {
  label: string;
  active?: boolean;
}

/** `POST /api/interests`. */
export interface CreateInterestResponse {
  interest: WorkInterestCategory;
}

/** `PATCH /api/interests/:id`. */
export interface UpdateInterestRequest {
  label?: string;
  active?: boolean;
}

/** `PATCH /api/interests/:id`. */
export interface UpdateInterestResponse {
  interest: WorkInterestCategory;
}

/** `DELETE /api/interests/:id` — soft-delete returns the deactivated category, hard delete `null`. */
export interface DeleteInterestResponse {
  ok: true;
  interest: WorkInterestCategory | null;
}

/* ------------------------------------------------------------------------------------------------
 * Admin API — host media and global videos
 * ---------------------------------------------------------------------------------------------- */

/** `GET /api/media` — the host video library, newest first. */
export interface ListMediaResponse {
  media: MediaAsset[];
}

/** `POST /api/media/upload-url` — reserve a Stream direct upload for a new host video. */
export interface MediaUploadUrlRequest {
  label: string;
  kind: MediaKind;
  uploadLength: number;
}

/** `POST /api/media/upload-url` — the TUS endpoint plus the ids the browser reports back. */
export interface MediaUploadUrlResponse {
  uploadUrl: string;
  streamVideoId: string;
  mediaId: string;
}

/** `POST /api/media/:id/finalize` — polls Stream and stores duration, thumbnail and status. */
export interface FinalizeMediaResponse {
  media: MediaAsset;
}

/** `DELETE /api/media/:id` — refused with `conflict` while the asset is still assigned. */
export type DeleteMediaResponse = OkResponse;

/** `GET /api/global-videos` — the current host video assignments. */
export interface GlobalVideosResponse {
  globalVideos: GlobalVideoSettings;
}

/** `PATCH /api/global-videos` — assign or clear (`null`) any of the four slots. */
export interface UpdateGlobalVideosRequest {
  welcomeMediaId?: string | null;
  introMediaId?: string | null;
  listeningMediaId?: string | null;
  closingMediaId?: string | null;
}

/** `PATCH /api/global-videos`. */
export interface UpdateGlobalVideosResponse {
  globalVideos: GlobalVideoSettings;
}

/* ------------------------------------------------------------------------------------------------
 * Admin API — settings
 * ---------------------------------------------------------------------------------------------- */

/** `GET /api/settings` — the full settings document including its audit fields. */
export interface SettingsResponse {
  settings: AppSettings;
}

/**
 * `PATCH /api/settings/content` — welcome, instructions, confirmation copy, the name and title of
 * the person on camera, the HR contact details, the two brand colours and `showHelpText`. Email is
 * the only contact route Altamont publishes: candidates may leave a number for HR to call out on,
 * but no number is offered to call in on.
 *
 * The colours are here, rather than under `/system`, because branding the product is the same job
 * as writing its words and belongs to the same `write` role — this mirrors `CONTENT_SETTINGS_KEYS`
 * in `routes/admin.ts`, which is what actually enforces it. `logoPath` is deliberately absent from
 * every patch shape: the only way to change it is to upload or remove a file through
 * `POST` / `DELETE /api/settings/logo`, so the stored path and the stored bytes cannot disagree.
 */
export type UpdateSettingsContentRequest = Partial<
  Pick<
    AppSettings,
    | 'hostName'
    | 'hostTitle'
    | 'brandPrimaryColor'
    | 'brandAccentColor'
    | 'showHelpText'
    | 'welcomeHeadline'
    | 'welcomeBody'
    | 'expectedDurationText'
    | 'generalInstructions'
    | 'hrContactName'
    | 'hrContactEmail'
    | 'accommodationsText'
    | 'confirmationHeadline'
    | 'confirmationBody'
  >
>;

/** `PATCH /api/settings/disclosure` — disclosure copy and the consent version it belongs to. */
export type UpdateSettingsDisclosureRequest = Partial<
  Pick<
    AppSettings,
    | 'disclosureTitle'
    | 'disclosureBody'
    | 'disclosureBullets'
    | 'consentCheckboxLabel'
    | 'consentVersion'
  >
>;

/**
 * `PATCH /api/settings/system` — the application on/off switch, the sentence shown while it is off,
 * the default answer length, and which AI provider writes the summaries. Four keys, and they are
 * the four in `SYSTEM_SETTINGS_KEYS`.
 *
 * This type used to also list `logoPath` and the branding keys, which the route has never accepted.
 * Nothing caught it, because a type that is wider than the endpoint it describes makes the compiler
 * vouch for a request the server rejects: the admin app dutifully sent the brand colours here and
 * got back `"brandPrimaryColor" is not part of the system settings.` at runtime. Branding lives on
 * {@link UpdateSettingsContentRequest}; the logo has its own upload route.
 */
export type UpdateSettingsSystemRequest = Partial<
  Pick<
    AppSettings,
    'applicationEnabled' | 'disabledMessage' | 'defaultMaxDurationSeconds' | 'aiProvider'
  >
>;

/** The settings document after any of the three settings patches. */
export interface UpdateSettingsResponse {
  settings: AppSettings;
}

/* ------------------------------------------------------------------------------------------------
 * Admin API — users and audit
 * ---------------------------------------------------------------------------------------------- */

/** `GET /api/users` — every HR user, including deactivated ones. */
export interface ListUsersResponse {
  users: AdminUser[];
}

/** `POST /api/users` — invite an Auth0 identity and give it a role. */
export interface CreateUserRequest {
  auth0Sub: string;
  email: string;
  name: string;
  role: AdminRole;
}

/** `POST /api/users`. */
export interface CreateUserResponse {
  user: AdminUser;
}

/** `PATCH /api/users/:id` — an admin may not demote or deactivate themselves. */
export interface UpdateUserRequest {
  role?: AdminRole;
  active?: boolean;
  name?: string;
}

/** `PATCH /api/users/:id`. */
export interface UpdateUserResponse {
  user: AdminUser;
}

/** `DELETE /api/users/:id` — an admin may not delete themselves. */
export type DeleteUserResponse = OkResponse;

/** Query-string parameters of `GET /api/audit`. */
export interface ListAuditQuery {
  applicationId?: string;
  limit?: number;
  cursor?: string;
}

/** `GET /api/audit` — one page of audit events, newest first. */
export interface ListAuditResponse {
  events: AuditEvent[];
  nextCursor: string | null;
}
